import React, { useState } from 'react';
import { Alert, Button } from '@mui/material';
import { Business as BusinessIcon, Sync as SyncIcon } from '@mui/icons-material';
import { authPost } from '../../services/apiService';
import NameIdRosterEditor from './NameIdRosterEditor';

const darkGreenColor = '#1D4434';

/**
 * Admin: Rank + Account Name + Salesforce ACCOUNT_ID for Top Customers.
 * Rank is required and unique (CSV import supported). Order no longer depends
 * on typing rows in sequence.
 */
const TopCustomerAccountsAdmin = () => {
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [syncError, setSyncError] = useState(null);

  const handleSyncCases = async () => {
    try {
      setSyncing(true);
      setSyncError(null);
      setSyncMessage(null);
      const response = await authPost(
        '/api/customer-issues/top-customers/accounts/sync',
        {},
        { timeout: 300000 }
      );
      if (response.success) {
        setSyncMessage(response.message || 'Top Customer cases synced to Mongo');
        setTimeout(() => setSyncMessage(null), 12000);
      } else {
        setSyncError(response.message || 'Failed to sync cases');
      }
    } catch (err) {
      console.error('Error syncing Top Customer cases:', err);
      setSyncError(err.response?.data?.message || err.message || 'Failed to sync cases');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <NameIdRosterEditor
      icon={BusinessIcon}
      title="Top Customer Accounts"
      description={(
        <>
          Enter Rank, Account Name, and Salesforce ACCOUNT_ID for each customer — or Import CSV
          (<code>Rank,Name,ACCOUNT_ID</code>, optionally with an <code>Instance_Name</code> column).
          Save updates the roster; then use{' '}
          <strong>Refresh cases from lake</strong> (or OE Sync) to pull HighQ cases into Mongo.
        </>
      )}
      infoAlert="Rank sets the Top Customers board order (must be unique). Same firm can appear on
        multiple rows with different ACCOUNT_IDs (e.g. Greenberg US duplicates)."
      listKey="accounts"
      idField="accountId"
      idFieldLabel="ACCOUNT_ID"
      idPlaceholder="e.g. 0011B00002B8KoEQAV"
      namePlaceholder="e.g. FOLEY & LARDNER LLP"
      extraFields={[
        { key: 'instanceName', label: 'Instance Name', placeholder: 'e.g. simmons' }
      ]}
      loadUrl="/api/customer-issues/top-customers/accounts"
      saveUrl="/api/customer-issues/top-customers/accounts"
      addButtonLabel="Add account"
      emptyStateText="No accounts yet — add a row with Rank, Account Name and ACCOUNT_ID, or Import CSV."
      enableRank
      enableCsv
      csvFileName="top-customer-accounts.csv"
      extraDisabled={syncing}
      buildSuccessMessage={(message) => `${message} — click "Refresh cases from lake" to write Top Customer cases into Mongo.`}
      renderExtraActions={({ disabled }) => (
        <Button
          variant="outlined"
          startIcon={<SyncIcon />}
          onClick={handleSyncCases}
          disabled={disabled}
          sx={{ borderColor: darkGreenColor, color: darkGreenColor }}
        >
          {syncing ? 'Refreshing cases…' : 'Refresh cases from lake'}
        </Button>
      )}
      footer={(
        <>
          {syncMessage && (
            <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSyncMessage(null)}>
              {syncMessage}
            </Alert>
          )}
          {syncError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSyncError(null)}>
              {syncError}
            </Alert>
          )}
        </>
      )}
    />
  );
};

export default TopCustomerAccountsAdmin;
